import { zodResolver } from "@hookform/resolvers/zod";
import React, { useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import FormFieldItem from "src/common/form/FormFieldItem";
import { Button } from "src/Components/ui/button";
import { DialogClose } from "src/Components/ui/dialog";
import { Form, FormField } from "src/Components/ui/form";
import { Input } from "src/Components/ui/input";
import { Label } from "src/Components/ui/label";
import { Switch } from "src/Components/ui/switch";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "src/Components/ui/card";
import { User, Phone, Wallet, ToggleLeft, CheckCircle2, XCircle } from "lucide-react";
import UAEDirhamIcon from "src/Components/utils/UAEDirhamIcon";

function CustomersForm(props) {
  const { t } = useTranslation();
  const dialogCloseRef = useRef(null);
  // make validation and form fields values conditionally
  const formSchema = z.object({
    name: z
      .string({
        required_error: `${t("gloabl.name")} ${t(
          "errorMessage.fieldRequired"
        )}.`,
      })
      .min(2, {
        message: `${t("gloabl.name")} ${t("errorMessage.fieldCharsLength")}.`,
      }),

    phone: z
      .string({
        required_error: `${t("gloabl.phone")} ${t(
          "errorMessage.fieldRequired"
        )}.`,
      })
      .min(2, {
        message: `${t("gloabl.phone")} ${t("errorMessage.fieldCharsLength")}.`,
      }),
    wallet: z.number({
      required_error: `${t("gloabl.wallet")} ${t(
        "errorMessage.fieldRequired"
      )}.`,
    }),
    active: z.any({
      required_error: `${t("gloabl.active")} ${t(
        "errorMessage.fieldRequired"
      )}.`,
    }),
  });
  // 1. Define your form.
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ...props.datasource,
    },
  });

  const onErorr = (errors) => {
    toast.error(t("errorMessage.fillAll"));
  };
  // 2. Define a submit handler.
  function onSubmit(values) {
    let submitValues = {
      ...values,
    };
    props.onSubmit(submitValues);
    if (dialogCloseRef.current) {
      dialogCloseRef.current.click();
    }
  }
  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit, onErorr)}
        className="space-y-6"
      >
        {/* Basic Information Section */}
        <Card>
          <CardHeader className="bg-blue-50 dark:bg-blue-950/30 border-b">
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              {t("gloabl.basic_info") || "Basic Information"}
            </CardTitle>
            <CardDescription>
              {t("gloabl.customer_basic_info_desc") ||
                "Enter customer name and contact information"}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <FormField
              control={form.control}
              name={"name_en"}
              render={({ field }) => (
                <FormFieldItem required form={form} label={t("gloabl.name")}>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <Input {...field} placeholder={t("gloabl.name") || "Customer name"} />
                  </div>
                </FormFieldItem>
              )}
            />
            <FormField
              control={form.control}
              name={"phone"}
              render={({ field }) => (
                <FormFieldItem required form={form} label={t("gloabl.phone")}>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <Input {...field} placeholder={t("gloabl.phone") || "Phone number"} />
                  </div>
                </FormFieldItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Wallet & Status Section */}
        <Card>
          <CardHeader className="bg-purple-50 dark:bg-purple-950/30 border-b">
            <CardTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-purple-600" />
              {t("gloabl.wallet_status") || "Wallet & Status"}
            </CardTitle>
            <CardDescription>
              {t("gloabl.wallet_status_desc") ||
                "Set wallet balance and account status"}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <FormField
              control={form.control}
              name={"wallet"}
              render={({ field }) => (
                <FormFieldItem required form={form} label={t("gloabl.wallet")}>
                  <div className="flex items-center gap-2">
                    <UAEDirhamIcon className="w-4 h-4 text-muted-foreground" />
                    <Input
                      type="number"
                      {...field}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                </FormFieldItem>
              )}
            />
            <FormField
              control={form.control}
              name="active"
              render={({ field }) => (
                <FormFieldItem form={form} label={t("gloabl.status")}>
                  <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
                    <div className="flex items-center gap-3">
                      {field.value ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                      <div>
                        <Label className="text-sm font-medium cursor-pointer">
                          {t("gloabl.active")}
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          {t("gloabl.customer_status_desc") ||
                            "Enable or disable customer account"}
                        </p>
                      </div>
                    </div>
                    <Switch
                      dir="ltr"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </div>
                </FormFieldItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t">
          <DialogClose asChild>
            <Button type="button" variant="outline" size="lg">
              {t("gloabl.cancel")}
            </Button>
          </DialogClose>
          <DialogClose ref={dialogCloseRef} />
          <Button type="submit" size="lg">
            {t("gloabl.save")}
          </Button>
        </div>
      </form>
    </Form>
  );
}

export default CustomersForm;
